import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
  createApi,
  defaultSerializeQueryArgs,
  fetchBaseQuery,
  retry,
} from "@reduxjs/toolkit/query/react";
import { message as messageToast } from "antd";
import { ApiResponse,AuthInterceptor, errorsKeys, getApiErrorMessage } from "./utils";
import { WarningIcon, CheckSuccessIcon } from "src/assets/Icons";
import Config from "../app.config";
import { findValueByKeys } from "src/utility";
import { isIntegratedSession } from "src/auth/isEmbedded";
import { AUTH_FAILED_MESSAGE_TYPE } from "src/auth/postMessageTypes";
import { postMessageToParent } from "src/auth/originUtils";
import { setTokenExpired} from "./slices/authentication/auth.slice";

messageToast.config({
  maxCount: 3,
  duration: 3,
});

const baseUrl = Config.INTERNAL_GATEWAY_URL;

const baseQuery = fetchBaseQuery({
  baseUrl,
  prepareHeaders: AuthInterceptor,
});

export const baseQueryWithRetry = retry(baseQuery, {maxRetries:1});



type CustomExtraOption = {
  notifyOnFailure?: boolean;
  notifyOnSuccess?: boolean;
};

type customBaseQuery = BaseQueryFn<string | FetchArgs, any, FetchBaseQueryError, CustomExtraOption, FetchBaseQueryMeta>;



const baseQueryWithNotification: customBaseQuery = async (args, api, extraOptions) => {
  const result = await baseQuery(args, api, extraOptions);
  if (result.error?.status === 401) {
    if (isIntegratedSession()) {
      postMessageToParent({ type: AUTH_FAILED_MESSAGE_TYPE });
    } else {
      api.dispatch(setTokenExpired(true));
    }
  }
  if (result.error && !!extraOptions && "notifyOnFailure" in extraOptions && extraOptions.notifyOnFailure) {
    const errorMessage = getApiErrorMessage(result.error);
    if (Array.isArray(errorMessage)) {
      errorMessage.forEach((error) => {
        const message = typeof error === "string" ? error : findValueByKeys(error, errorsKeys) ?? "An error occurred";
        messageToast.open({
          type: "warning",
          content: message,
          className: "custom-warning-message",
          icon:<span className="flex h-7 w-7 items-center justify-center"><WarningIcon /></span>,
        });
      });
    } else {
      messageToast.open({
        type: "warning",
        content: errorMessage,
        className: "custom-warning-message",
        icon: <span className="flex h-7 w-7 items-center justify-center"><WarningIcon /></span>,
      });
    }
  } else if (result.data && !!extraOptions && "notifyOnSuccess" in extraOptions && extraOptions.notifyOnSuccess) {
    const response = result.data as ApiResponse;
    messageToast.open({
      type: "success",
      content: response.message || "Process completed successfully",
      className: "custom-success-message",
      icon: <span className="flex items-center justify-center w-7 h-7"><CheckSuccessIcon /></span>,
    });
  }
  return result;
};

const BaseApi = createApi({
  reducerPath: "BaseApi",
  baseQuery: baseQueryWithNotification,
  endpoints: () => ({}),
  serializeQueryArgs: ({ endpointName, queryArgs, endpointDefinition }) => {
    return defaultSerializeQueryArgs({
      endpointName,
      queryArgs,
      endpointDefinition,
    });
  },
});

export default BaseApi;
